import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { productionApi } from '../../services/productionApi.js';
import { formatDateTime, formatNumber } from '../../utils/formatters.js';

export function ConfirmWorkOrderModal({ workOrder, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ quantity: '', scrapQuantity: '', laborHours: '' });
  const [error, setError] = useState(null);

  const wo = workOrder;
  const remaining = wo ? Number(wo.quantity) - Number(wo.confirmedQuantity || 0) : 0;

  const confs = useQuery({ queryKey: ['production', 'wo-confirmations', wo?.id], queryFn: () => productionApi.listWorkOrderConfirmations(wo.id).then((r) => r.items), enabled: Boolean(wo) });

  useEffect(() => { if (wo) { setForm({ quantity: String(remaining), scrapQuantity: '', laborHours: '' }); setError(null); } }, [wo?.id]); // eslint-disable-line

  const save = useMutation({
    mutationFn: () => productionApi.confirmWorkOrder(wo.id, { quantity: Number(form.quantity), scrapQuantity: Number(form.scrapQuantity) || 0, laborHours: Number(form.laborHours) || 0 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production', 'work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production', 'wo-confirmations', wo.id] });
      onClose();
    },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.quantity || Number(form.quantity) <= 0) { setError(t('pc2.error.qty')); return; }
    if (Number(form.quantity) > remaining + 1e-6) { setError(t('pc2.error.over', { remaining: formatNumber(remaining) })); return; }
    save.mutate();
  }

  return (
    <Modal open={Boolean(wo)} onClose={onClose}
      title={wo ? `${t('pc2.title')} · ${wo.orderNumber}` : t('pc2.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('pc2.action.confirm')}…` : t('pc2.action.confirm')}</Button></>}>
      {wo && (
        <form onSubmit={submit} className="form-stack">
          <p className="muted text-sm">{wo.productSku} · {t('pc2.confirmed')}: <strong>{formatNumber(wo.confirmedQuantity || 0)}</strong> / {formatNumber(wo.quantity)} · {t('pc2.remaining')}: <strong>{formatNumber(remaining)}</strong></p>
          <div className="field-row">
            <label className="field" style={{ maxWidth: 150 }}><span className="field-label">{t('pc2.quantity')}</span>
              <input className="input num" type="number" min="0" step="any" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} required />
            </label>
            <label className="field" style={{ maxWidth: 150 }}><span className="field-label">{t('pc2.scrap')}</span>
              <input className="input num" type="number" min="0" step="any" value={form.scrapQuantity} onChange={(e) => setForm((f) => ({ ...f, scrapQuantity: e.target.value }))} />
            </label>
            <label className="field" style={{ maxWidth: 150 }}><span className="field-label">{t('pc2.labor')}</span>
              <input className="input num" type="number" min="0" step="any" value={form.laborHours} onChange={(e) => setForm((f) => ({ ...f, laborHours: e.target.value }))} />
            </label>
          </div>
          {error && <div className="alert danger">{error}</div>}

          {(confs.data || []).length > 0 && (
            <>
              <div className="settings-divider">{t('pc2.history')}</div>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>{t('pc2.col.number')}</th><th className="num">{t('pc2.quantity')}</th><th className="num">{t('pc2.scrap')}</th><th>{t('pc2.col.time')}</th></tr></thead>
                  <tbody>
                    {(confs.data || []).map((c) => (
                      <tr key={c.id}><td className="mono">{c.confirmationNumber}</td><td className="num mono">{formatNumber(c.confirmedQuantity)}</td><td className="num mono">{formatNumber(c.scrapQuantity)}</td><td className="muted text-sm">{formatDateTime(c.createdAt)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </form>
      )}
    </Modal>
  );
}
